import { apiClient } from "@/api";
import { ApiResponse, PaginatedData } from "@/types";
import { AxiosRequestConfig } from "axios";

export type ProjectStatus =
  | "in_progress"
  | "under_review"
  | "completed"
  | "deleted";

export interface GetListPrjReq {
  search?: string;
  status?: ProjectStatus;
  page: number;
  page_size: number;
}

export interface ProjectRes {
  id: string;
  name: string;
  creator_name: string;
  status: ProjectStatus;
  agent_name: string;
  created_at: string;
}

export interface PorjectDetaiRes {
  id: string;
  name: string;
  description: string;
  creator_name: string;
  status: ProjectStatus;
  has_report: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectReport {
  "1. 적용 업무 범위": ScopeDefinition;
  "2. 도입 목적": IntroductionPurpose;
  "3. 자동화 수준 정의": AutomationLevelDefinition;
  "4. 보안 및 시스템 연계 요구사항": SecurityAndIntegration;
  "5. 데이터 및 데이터 파이프라인 요건": DataPipelineRequirement;
  "6. Agent 적합성 필터 (요약)": AgentSuitabilityFilter;

  // Legacy support — old reports may still have this key instead of
  // "4. 보안 및 시스템 연계 요구사항"
  "4. 보안 정책"?: SecurityPolicyLegacy;

  id: string;
  "문제 상황 정의": string;
  "예산 범위"?: BudgetRange;
  프로젝트명: string;
  요약: string;
}

export interface ProjectReportRes {
  id: string;
  project_id: string;
  project_name: string;
  summary: string;
  problem: string;
  content: ProjectReport;
  project_status: ProjectStatus;
  created_at: string;
  updated_at: string;
}
interface BudgetRange {
  "투자 의향 예산": string | null;
}

interface ScopeDefinition {
  "In scope": string[];
}

interface IntroductionPurpose {
  "도입 목적": string;
}

interface AutomationLevelDetails {
  [key: string]: string;
}

interface AutomationLevelDefinition {
  "자동화 수준": string;
  "자동화 레벨 분류 및 세부 요건"?: AutomationLevelDetails;
  "Human-in-the-loop": string;
}

interface SystemIntegrationRequirements {
  "대상 시스템": string;
  "연계 방식 및 방향성": string;
  "데이터 지연 허용 범위": string;
}

export interface SecurityAndIntegration {
  "시스템 구축 방식": string;
  "보안 정책": string;
  "시스템 연계 요구사항"?: SystemIntegrationRequirements;
}

// Legacy: old report format
export interface SecurityPolicyLegacy {
  "LLM 구축 방식": string;
  "보안 정책": string;
}

interface DataPipelineRequirement {
  "데이터 수집 및 정제 요구사항"?: string;
  "데이터 제약조건 및 검증 범위"?: string;
  // Legacy field
  "데이터 수집 및 정제"?: string;
}

interface AgentSuitabilityFilter {
  "적합 유형": string;
}

export interface IUpdateStatusPrjReq {
  status: ProjectStatus;
}

export interface EditPrjReportRequest {
  project_id: string;
  content: ProjectReport;
}
export const ProjectService = {
  getListProject: (params: GetListPrjReq, config?: AxiosRequestConfig) => {
    return apiClient.get<ApiResponse<PaginatedData<ProjectRes>>>(
      "admin/projects/",
      { ...config, params },
    );
  },
  getProjectById: (id: string, config?: AxiosRequestConfig) => {
    return apiClient.get<ApiResponse<PorjectDetaiRes>>(`admin/projects/${id}`, config);
  },
  deleteProject: (id: string, config?: AxiosRequestConfig) => {
    return apiClient.delete(`admin/projects/${id}`, config);
  },
  updateProjectStatus: (id: string, req: IUpdateStatusPrjReq, config?: AxiosRequestConfig) => {
    return apiClient.put(`admin/projects/${id}/status`, req, config);
  },
  getProjectReport: (projectId: string, config?: AxiosRequestConfig) => {
    return apiClient.get<ApiResponse<ProjectReportRes>>(
      `admin/projects/${projectId}/report`,
      config
    );
  },
  editProjectReport: (req: EditPrjReportRequest, config?: AxiosRequestConfig) => {
    return apiClient.put<ApiResponse<ProjectReportRes>>(
      `admin/projects/${req.project_id}/report`,
      { content: req.content },
      config
    );
  },

  completeProject: (projectId: string, config?: AxiosRequestConfig) => {
    return apiClient.post(`admin/projects/${projectId}/complete`, undefined, config);
  },
};
